// checklistModels.js

// Firebase
import { Timestamp } from 'firebase/firestore';

// Utilities
import { generateKey } from 'utilities/Keys';

const normalizeString = (value) => String(value || '').trim();

function normalizeChecklistItemAssignees(value) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const map = new Map();

  list.forEach((entry) => {
    const key = normalizeString(entry?.userKey || entry?.key);
    if (!key) return;

    const name = normalizeString(
      entry?.name
      || entry?.data_profile?.name
      || entry?.title
      || entry?.email
      || key
    );

    map.set(key, {
      key,
      userKey: key,
      name,
      title: normalizeString(entry?.title) || name,
      email: normalizeString(entry?.email || entry?.data_profile?.email),
      coverUrl: normalizeString(entry?.coverUrl),
      data_profile: {
        name,
        email: normalizeString(entry?.email || entry?.data_profile?.email),
      },
    });
  });

  return Array.from(map.values());
}

function normalizeChecklistSourceItems(value) {
  const list = Array.isArray(value) ? value : [];

  return list.flatMap((entry) => {
    if (Array.isArray(entry?.items)) {
      return entry.items;
    }

    return entry ? [entry] : [];
  });
}

export function normalizeChecklistTemplateItems(items) {
  return normalizeChecklistSourceItems(items)
    .map((item, itemIndex) => {
      const text = normalizeString(item?.text);
      if (!text) return null;

      return {
        key: normalizeString(item?.key) || generateKey(),
        text,
        position: Number.isFinite(Number(item?.position)) ? Number(item.position) : itemIndex,
      };
    })
    .filter(Boolean);
}

export function createChecklistSnapshotItems(items) {
  const now = Timestamp.now();

  return normalizeChecklistSourceItems(items)
    .map((item, itemIndex) => {
      const text = normalizeString(item?.text);
      if (!text) return null;

      return {
        key: normalizeString(item?.key) || generateKey(),
        text,
        position: Number.isFinite(Number(item?.position)) ? Number(item.position) : itemIndex,
        status: item?.status === 'done' ? 'done' : 'todo',
        completedAt: item?.status === 'done' ? (item?.completedAt || now) : null,
        assignees: normalizeChecklistItemAssignees(item?.assignees),
      };
    })
    .filter(Boolean);
}

export function deriveChecklistAssignmentKeys(items) {
  const normalizedItems = normalizeChecklistSourceItems(items);
  const assigneeKeys = new Set();
  const openAssigneeKeys = new Set();

  normalizedItems.forEach((item) => {
    const itemAssignees = normalizeChecklistItemAssignees(item?.assignees);
    const isOpen = String(item?.status || '').trim().toLowerCase() !== 'done';

    itemAssignees.forEach((assignee) => {
      const assigneeKey = normalizeString(assignee?.userKey || assignee?.key);
      if (!assigneeKey) return;

      assigneeKeys.add(assigneeKey);

      if (isOpen) {
        openAssigneeKeys.add(assigneeKey);
      }
    });
  });

  return {
    assigneeKeys: Array.from(assigneeKeys).sort((left, right) => left.localeCompare(right)),
    openAssigneeKeys: Array.from(openAssigneeKeys).sort((left, right) => left.localeCompare(right)),
  };
}

export function computeChecklistProgress(items) {
  const normalizedItems = normalizeChecklistSourceItems(items);

  return normalizedItems.reduce((accumulator, item) => {
    accumulator.totalItems += 1;
    accumulator.requiredItems += 1;

    if (item?.status === 'done') {
      accumulator.completedItems += 1;
      accumulator.requiredCompletedItems += 1;
    }

    return accumulator;
  }, {
    totalItems: 0,
    completedItems: 0,
    requiredItems: 0,
    requiredCompletedItems: 0,
  });
}

export function deriveChecklistStatus(progress, explicitStatus) {
  const requested = normalizeString(explicitStatus).toLowerCase();
  if (requested === 'blocked') return 'blocked';
  if (requested === 'archived') return 'archived';

  const totalItems = Number(progress?.totalItems) || 0;
  const completedItems = Number(progress?.completedItems) || 0;

  if (totalItems > 0 && completedItems >= totalItems) return 'completed';
  if (completedItems > 0) return 'in_progress';
  return 'not_started';
}

export function normalizeChecklistAnchor(anchor) {
  const kind = normalizeString(anchor?.kind).toLowerCase();

  if (kind !== 'node') return null;

  const nodeKey = normalizeString(anchor?.nodeKey);
  if (!nodeKey) return null;

  return {
    kind: 'node',
    nodeKey,
    nodeType: normalizeString(anchor?.nodeType) || null,
    fieldName: normalizeString(anchor?.fieldName) || 'checklist',
    label: normalizeString(anchor?.label) || null,
  };
}